import type { Week } from '@/lib/types';

/**
 * 주간회의 화면을 <b>어느 주차로 열까</b>.
 *
 * <h2>화요일부터는 다음 주다 (사용자 결정 2026-09-29)</h2>
 * 「오늘이 든 주」로 열던 자리다. 그런데 주간회의는 <b>월요일에 이번 주 것을 확정하고, 그 뒤로는
 * 다음 주를 채우는</b> 일이다 — 화요일에 열어서 이번 주 칸이 서면, 이미 지나간 계획 위에 적으려다
 * 매번 주차를 한 칸 넘긴 뒤에야 쓰기 시작한다. 한 칸 넘기는 그 동작이 <b>모든 사람의 모든 날</b>에
 * 붙는다.
 *
 * <p>그래서 <b>월요일 하루만</b> 이번 주고, 화요일부터 일요일까지는 다음 주로 연다. 주말도 다음
 * 주다(사용자 결정) — 토·일에 여는 사람은 다가올 주를 준비하는 것이지 끝난 주를 보려는 것이 아니다.
 *
 * <p>여기는 <b>처음 열었을 때</b>의 자리일 뿐이다. 넘기는 화살표와 고르는 상자는 그대로 있으므로,
 * 지난주를 보려는 사람이 못 보게 되는 것은 없다.
 *
 * <h2>웹과 앱이 같은 값을 말해야 한다</h2>
 * 같은 규칙이 웹 `useWeekSelection` 과 앱 `useWeekCursor`·주간업무 화면에 따로 적혀 있으면, 한쪽만
 * 고친 날 두 화면이 서로 다른 주차를 열어 놓고 <b>둘 다 「이번 주」라고 말한다</b>. 그래서 한 곳에
 * 둔다 — 앱은 이 파일을 그대로 가져다 쓴다(`@/lib/...` 별칭이 앱에도 걸려 있다).
 *
 * @param weeks 주차 목록. <b>차례는 묻지 않는다</b> — 웹은 최근 순, 앱은 따로 정렬해 쓴다.
 * @param today 오늘. 시험에서만 넘긴다.
 * @return 열 주차. 목록이 비었으면 `null`.
 */
export function openingWeek(weeks: Week[], today: Date = new Date()): Week | null {
  if (weeks.length === 0) return null;

  const key = isoDay(today);
  const current = weeks.find((week) => week.startDate <= key && key <= week.endDate);

  // 오늘이 어느 주차에도 안 들면 <b>가장 최근 주차</b>다(종전과 같다). 주차 표가 아직 이번 달까지
  // 안 깔렸을 때인데, 그때 빈 화면을 세우는 것보다는 마지막으로 채운 주를 보여 주는 편이 낫다.
  if (!current) return latest(weeks);

  // 0=일 … 1=월. 월요일 하루만 이번 주다.
  if (today.getDay() === MONDAY) return current;

  /*
    다음 주 = <b>이번 주가 끝난 뒤 가장 먼저 시작하는</b> 주차. 「목록에서 한 칸 앞」으로 세지 않는다 —
    웹과 앱의 정렬이 반대라 그 셈은 한쪽에서 지난주를 집는다.
  */
  const next = weeks
    .filter((week) => week.startDate > current.endDate)
    .sort((a, b) => a.startDate.localeCompare(b.startDate))[0];

  // 다음 주가 아직 표에 없으면 이번 주에 선다. 없는 주차를 지어내지 않는다.
  return next ?? current;
}

const MONDAY = 1;

/** 그 자리의 오늘. `Date.toISOString()` 은 UTC 라 자정 언저리에 하루가 밀린다. */
export function isoDay(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function latest(weeks: Week[]): Week {
  return [...weeks].sort((a, b) => b.startDate.localeCompare(a.startDate))[0] as Week;
}
